/*
 * Derpy Cards — frontend enhancements
 * ------------------------------------------------------------------
 * Phase 1: Make product cards open the QUICK-VIEW popup instead of
 * navigating to a full product page, and give that popup a clean,
 * shareable /products/{handle} URL via the History API (so links are
 * SEO-friendly and the browser back button just closes the popup).
 *
 * Self-contained: depends only on stable theme DOM that is rendered
 * globally in layout/theme.liquid (#Product-Drawer, #Product-Drawer-Content,
 * .click-capture--product, body.open-cc--product). It does NOT modify the
 * bundled app.js, so a theme update can't silently break it.
 * Full product pages remain crawlable canonical fallbacks
 * (cmd/ctrl/middle-click still open them in a new tab).
 */
(function () {
  'use strict';

  var rootUrl = (window.theme && theme.routes && theme.routes.root_url) || '';
  if (rootUrl === '/') rootUrl = '';

  var drawer  = function () { return document.getElementById('Product-Drawer'); };
  var content = function () { return document.getElementById('Product-Drawer-Content'); };
  var scrim   = function () { return document.querySelector('.click-capture--product'); };

  var state = { open: false, pushed: false, returnUrl: null, trigger: null, token: 0 };

  var SKELETON =
    '<div class="derpy-qv-skeleton">' +
    '<div class="derpy-qv-skeleton__img derpy-skeleton"></div>' +
    '<div class="derpy-qv-skeleton__line sm derpy-skeleton"></div>' +
    '<div class="derpy-qv-skeleton__line lg derpy-skeleton"></div>' +
    '<div class="derpy-qv-skeleton__line derpy-skeleton"></div>' +
    '<div class="derpy-qv-skeleton__line price derpy-skeleton"></div>' +
    '<div class="derpy-qv-skeleton__btn derpy-skeleton"></div>' +
    '</div>';

  function handleFromUrl(url) {
    var m = /\/products\/([^/?#]+)/.exec(url || '');
    return m ? m[1] : null;
  }
  function cleanProductUrl(handle) {
    return (rootUrl + '/products/' + handle).replace('//', '/');
  }

  function openDrawerDom() {
    var d = drawer(); if (!d) return;
    document.body.classList.add('open-cc--product');
    d.classList.add('active');
    d.removeAttribute('inert');
    d.setAttribute('tabindex', '-1');
    try { d.focus(); } catch (e) {}
  }
  function doClose() {
    state.open = false;
    state.closing = false;
    state.token++;                 // any quick view still loading must not re-open
    var d = drawer();
    if (d) {
      document.body.classList.remove('open-cc--product');
      d.classList.remove('active');
      d.setAttribute('inert', '');
    }
    var c = content();
    if (c) setTimeout(function () { if (!state.open) c.innerHTML = ''; }, 300);
    /* the theme rewrites history state (variant picks, add-to-cart), so the
       back() pop is not guaranteed — never leave /products/… in the bar */
    if (state.returnUrl && /\/products\//.test(location.pathname)) {
      try { history.replaceState(null, '', state.returnUrl); } catch (e) {}
    }
    var t = state.trigger;
    state.trigger = null; state.pushed = false; state.returnUrl = null;
    if (t && typeof t.focus === 'function') { try { t.focus(); } catch (e) {} }
  }

  function fetchAndOpen(handle, opts) {
    var d = drawer(), c = content();
    if (!d || !c) { window.location.href = cleanProductUrl(handle); return; }
    var url = (rootUrl + '/products/' + handle + '?view=quick-view').replace('//', '/');
    var token = ++state.token;
    if (opts && opts.trigger) opts.trigger.classList.add('loading');
    c.innerHTML = SKELETON;        // instant feedback while the section fetches
    openDrawerDom();
    fetch(url, { method: 'GET' })
      .then(function (r) { return r.text(); })
      .then(function (text) {
        if (opts && opts.trigger) opts.trigger.classList.remove('loading');
        if (token !== state.token || !state.open) return; // closed or superseded while loading
        var inner = new DOMParser().parseFromString(text, 'text/html')
          .querySelector('#Product-Drawer-Content');
        /* an unpublished/deleted product renders the drawer EMPTY — go to the
           product URL (Shopify's 404) instead of showing a blank panel */
        if (!inner || !inner.textContent.trim()) { window.location.href = cleanProductUrl(handle); return; }
        c.innerHTML = inner.innerHTML;
        // Re-execute any inline/linked scripts that came with the section
        c.querySelectorAll('script').forEach(function (s) {
          var n = document.createElement('script');
          if (s.src) { n.src = s.src; } else { n.textContent = s.textContent; }
          document.head.appendChild(n);
        });
        setTimeout(function () {
          try { if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init(); } catch (e) {}
          try { if (window.ProductModel) window.ProductModel.loadShopifyXR(); } catch (e) {}
        }, 300);
        openDrawerDom();
      })
      .catch(function () {
        if (opts && opts.trigger) opts.trigger.classList.remove('loading');
        if (token !== state.token || !state.open) return;
        window.location.href = cleanProductUrl(handle);
      });
  }

  function open(handle, opts) {
    if (!handle) return;
    opts = opts || {};
    var wasOpen = state.open;
    if (!wasOpen) {
      state.returnUrl = location.pathname + location.search + location.hash;
      state.trigger = opts.trigger || null;
    }
    state.open = true;
    fetchAndOpen(handle, opts);
    try {
      /* one history entry per quick-view session: a product opened from
         inside the drawer replaces the entry, so Back / close always lands
         on the gallery */
      if (wasOpen && state.pushed) history.replaceState({ derpyQuickView: handle }, '', cleanProductUrl(handle));
      else { history.pushState({ derpyQuickView: handle }, '', cleanProductUrl(handle)); state.pushed = true; }
    } catch (e) {}
  }

  function uiClose() {
    if (!state.open || state.closing) return; // closing: a history.back() is already in flight — never pop twice
    state.closing = true;
    if (state.pushed && /\/products\//.test(location.pathname)) {
      history.back();            // pops the quick-view entry -> popstate -> doClose()
      setTimeout(function () { if (state.open) doClose(); }, 400); // no popstate (e.g. entry was replaced) — close anyway
    } else {
      doClose();
    }
  }

  // --- Card click -> quick-view popup -------------------------------------
  document.addEventListener('click', function (e) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return; // new-tab intents
    var link = e.target.closest && e.target.closest('a.product-card--image-link, a.product-card-title');
    if (!link) return;
    if (!link.closest('.product-card, product-card')) return;
    var handle = handleFromUrl(link.getAttribute('href'));
    if (!handle) return;
    e.preventDefault();
    open(handle, { trigger: link });
  }, false);

  // --- Theme's own quick-view (e.g. sold-out buttons) -> also push URL -----
  document.addEventListener('quick-view:open', function (e) {
    var handle = e && e.detail && e.detail.productHandle;
    if (!handle || state.open) return;
    state.returnUrl = location.pathname + location.search + location.hash;
    state.open = true;
    try {
      history.pushState({ derpyQuickView: handle }, '', cleanProductUrl(handle));
      state.pushed = true;
    } catch (err) {}
  });

  // --- Close affordances ---------------------------------------------------
  document.addEventListener('click', function (e) {
    var s = scrim();
    var closeBtn = e.target.closest && e.target.closest('#Product-Drawer side-panel-close, #Product-Drawer .side-panel-close, #Product-Drawer [data-drawer-close]');
    if ((s && e.target === s) || closeBtn) uiClose();
  }, false);

  document.addEventListener('keydown', function (e) {
    if (!state.open) return;
    if ((e.key === 'Escape' || e.key === 'Esc') && !document.body.classList.contains('derpy-zoom-lock')) uiClose();
  });

  // Back / forward button closes the quick-view and restores the list URL
  window.addEventListener('popstate', function () {
    if (state.open) doClose();
  });

  /* The theme can close the drawer itself (its Esc/overlay "panel:close"),
     which left /products/… in the address bar. Treat that as our close. */
  var dEl = drawer();
  if (dEl && window.MutationObserver) {
    new MutationObserver(function () {
      if (state.open && !state.closing && !dEl.classList.contains('active')) uiClose();
    }).observe(dEl, { attributes: true, attributeFilter: ['class'] });
  }

  window.DerpyQuickView = { open: open, close: uiClose };
})();


/*
 * Collection: top "Filters & Sort" bar + off-canvas drawer for the Cloud Search
 * filter app. Keeps the app's real filters working (same DOM, just repositioned
 * by CSS) and frees the full width for a denser grid. No-ops on non-collection pages.
 */
(function () {
  'use strict';
  var built = false;
  var root = (window.theme && theme.routes && theme.routes.root_url) || '';
  if (root === '/') root = '';

  var IC = {
    filter: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>',
    x: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
    search: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></svg>',
    chev: '<svg class="derpy-fd__chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"></polyline></svg>'
  };

  var IN_STOCK_KEY = 'filter_availability[in_stock]';
  var DEFAULT_SORT = 'created-descending';
  var SORTS = [
    ['created-descending', 'Newest'],
    ['price-ascending', 'Price: Low to High'],
    ['price-descending', 'Price: High to Low']
  ];

  /* The gallery handle and path are captured ONCE: quick view pushes
     /products/<handle> into the address bar, and anything reading
     location.pathname while it is open got null (→ /a/search/null). */
  var GALLERY_PATH = location.pathname;
  var HANDLE = (function () { var m = /\/(?:collections|a\/search)\/([^/?#]+)/.exec(GALLERY_PATH); return m ? m[1] : null; })();
  /* Store-wide search: the Cloud Search app rewrites the header search form to
     /a/search?q=… — same filter groups as a gallery, but in a different
     container, no category handle, and `q` instead of filter_search. */
  var IS_SEARCH = !HANDLE && /^\/a\/search\/?$/.test(GALLERY_PATH);
  function handle() { return HANDLE; }
  function qp() { return new URLSearchParams(location.search); }
  function navTo(p) {
    p.delete('page');
    var qs = p.toString();
    var base = IS_SEARCH ? root + '/a/search' : root + '/a/search/' + HANDLE;
    location.assign(base + (qs ? ('?' + qs) : ''));
  }
  function prettyName(key) {
    var pm = /^filter_price\[(min|max)\]$/.exec(key);
    if (pm) return pm[1] === 'min' ? 'Price from' : 'Price up to';
    var n = key.replace(/^filter_/, '').replace(/_filter$/, '').replace(/\[.*$/, '').replace(/_/g, ' ');
    return n.charAt(0).toUpperCase() + n.slice(1);
  }

  function build() {
    if (built) return;
    if (!HANDLE && !IS_SEARCH) return;
    var sidebar, products, grid = document.getElementById('product-grid');
    if (IS_SEARCH) {
      sidebar = document.querySelector('#cloud_search_filters_root .cloud-search-page-controls__filters');
      products = grid ? grid.parentNode : null;
    } else {
      sidebar = document.getElementById('cloud_search_filters_sidebar');
      products = document.querySelector('.sidebar-container--products');
    }
    if (!sidebar || !products) return;
    if (!sidebar.querySelector('.cloud-search-filter')) return; // wait for app to inject filters
    built = true;

    var p = qp();

    /* off-canvas "More filters" drawer plumbing */
    var scrim = document.createElement('div'); scrim.className = 'derpy-filter-scrim'; document.body.appendChild(scrim);
    var closeBtn = document.createElement('button'); closeBtn.type = 'button'; closeBtn.className = 'derpy-filter-close';
    closeBtn.setAttribute('aria-label', 'Close filters'); closeBtn.innerHTML = IC.x; document.body.appendChild(closeBtn);
    function openD() { document.body.classList.add('derpy-filters-open'); }
    function closeD() { document.body.classList.remove('derpy-filters-open'); }
    closeBtn.addEventListener('click', closeD); scrim.addEventListener('click', closeD);
    document.addEventListener('keydown', function (e) { if ((e.key === 'Escape' || e.key === 'Esc') && document.body.classList.contains('derpy-filters-open')) closeD(); });

    var bar = document.createElement('div'); bar.className = 'derpy-toolbar';

    /* Category name (for the scoped-search pill + the relocated breadcrumb) */
    var crumbNav = document.querySelector('#shopify-section-breadcrumbs nav.breadcrumbs');
    var catName = '';
    if (crumbNav) { var bcl = crumbNav.querySelectorAll('a'); if (bcl.length) catName = bcl[bcl.length - 1].textContent.trim(); }
    if (!catName) { var hcat = handle(); catName = hcat ? prettyName('filter_' + hcat) : 'this category'; }

    /* Search box. Galleries: search within the category (filter_search).
       Store-wide search page: edits the query itself (q). */
    var SEARCH_KEY = IS_SEARCH ? 'q' : 'filter_search';
    var sform = document.createElement('form'); sform.className = 'derpy-tb-search'; sform.setAttribute('role', 'search');
    sform.innerHTML = IC.search;
    var sinput = document.createElement('input'); sinput.type = 'search'; sinput.className = 'derpy-tb-search__input';
    /* the phone search pill is ~115px of text: long category names were cut
       mid-word ("Search Poker…") — use a short placeholder there */
    var narrow = window.matchMedia && window.matchMedia('(max-width: 768px), (max-height: 500px) and (pointer: coarse)').matches;
    sinput.placeholder = narrow ? 'Search cards…' : (IS_SEARCH ? 'Search all cards…' : 'Search ' + catName + '…');
    sinput.value = p.get(SEARCH_KEY) || '';
    sinput.setAttribute('aria-label', IS_SEARCH ? 'Search the store' : 'Search within ' + catName);
    sform.appendChild(sinput);
    sform.addEventListener('submit', function (e) {
      e.preventDefault(); var np = qp(); var v = sinput.value.trim();
      if (v) np.set(SEARCH_KEY, v);
      else if (IS_SEARCH) return; // an empty store-wide search has nothing to show
      else np.delete(SEARCH_KEY);
      navTo(np);
    });

    /* In-stock toggle (galleries default on; search defaults off) */
    var inStock = p.get(IN_STOCK_KEY) === 'true';
    var stockBtn = document.createElement('button'); stockBtn.type = 'button';
    stockBtn.className = 'derpy-tb-toggle' + (inStock ? ' is-on' : ''); stockBtn.setAttribute('aria-pressed', inStock ? 'true' : 'false');
    stockBtn.innerHTML = '<span class="derpy-tb-toggle__track"><span class="derpy-tb-toggle__dot"></span></span><span>In stock</span>';
    stockBtn.addEventListener('click', function () {
      var np = qp();
      if (inStock) { np.delete(IN_STOCK_KEY); if (!IS_SEARCH && !np.get('sort_by')) np.set('sort_by', DEFAULT_SORT); }
      else { np.set(IN_STOCK_KEY, 'true'); }
      navTo(np);
    });

    /* Sort. Galleries: Newest default. Search: the app's own options, with
       Relevance as the default. */
    var sortList = SORTS, defaultSort = DEFAULT_SORT;
    if (IS_SEARCH) {
      var appSort = document.querySelector('#cloud_search_filters_root select');
      if (appSort && appSort.options.length) {
        sortList = [].map.call(appSort.options, function (o) { return [o.value, o.textContent.replace(/\s+/g, ' ').trim()]; });
      } else {
        sortList = [['relevance', 'Relevance']].concat(SORTS);
      }
      defaultSort = 'relevance';
    }
    var cur = p.get('sort_by') || defaultSort;
    var sortWrap = document.createElement('div'); sortWrap.className = 'derpy-tb-sort';
    sortWrap.innerHTML = '<span class="derpy-tb-sort__label">Sort</span><span class="derpy-tb-select"><select aria-label="Sort by"></select></span>';
    var sortSel = sortWrap.querySelector('select');
    sortList.forEach(function (o) {
      var opt = document.createElement('option'); opt.value = o[0]; opt.textContent = o[1];
      if (o[0] === cur) opt.selected = true;
      sortSel.appendChild(opt);
    });
    sortSel.addEventListener('change', function () {
      var np = qp();
      if (IS_SEARCH && this.value === 'relevance') np.delete('sort_by'); else np.set('sort_by', this.value);
      navTo(np);
    });

    /* Individual facet dropdowns — one button per filter, each opening its own
       dropdown straight from the toolbar (no monolithic "All filters" panel). */
    var SKIP_FACET = /search|availab|in stock|sort/i;
    var facetDropdowns = [];
    var openFD = null;
    function isExpanded(g) {
      if (!g) return false;
      /* search page: the app sets aria-expanded. Galleries: it sets no state
         at all — an expanded group simply HAS its value list rendered */
      return g.getAttribute('aria-expanded') === 'true' || g.classList.contains('cloud-search-filter--open') ||
        !!g.querySelector('.cloud-search-filter__values, .cloud-search-filter-value');
    }
    function closeAllFD() {
      if (!openFD) return;
      /* search page: the app's values live in a popover portalled to <body> —
         collapse the app's group too, or the popover stays on screen */
      if (IS_SEARCH) {
        var g = openFD.fd.querySelector('.cloud-search-filter');
        if (isExpanded(g)) { var n = g.querySelector('.cloud-search-filter__name'); try { (n || g).click(); } catch (err) {} }
      }
      openFD.fd.classList.remove('is-open'); openFD.btn.setAttribute('aria-expanded', 'false'); openFD = null;
    }
    [].forEach.call(sidebar.querySelectorAll('.cloud-search-filter'), function (group) {
      var nameEl = group.querySelector('.cloud-search-filter__name');
      /* an active facet's name carries its value, and Language values carry
         the app's "Language_" prefix ("Language: Language_Japanese") */
      var label = nameEl ? nameEl.textContent.replace(/\s+/g, ' ').trim().replace(/(:\s*)language[_\s]+/ig, '$1') : '';
      if (!label || SKIP_FACET.test(label)) { group.style.display = 'none'; return; }
      var fd = document.createElement('div');
      fd.className = 'derpy-fd';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'derpy-fd__btn';
      btn.setAttribute('aria-haspopup', 'true');
      btn.setAttribute('aria-expanded', 'false');
      var btnText = document.createElement('span'); btnText.textContent = label;
      btn.appendChild(btnText);
      btn.insertAdjacentHTML('beforeend', IC.chev);
      var panel = document.createElement('div');
      panel.className = 'derpy-fd__panel';
      panel.appendChild(group); // relocate the app's filter group into this dropdown
      fd.appendChild(btn);
      fd.appendChild(panel);

      // Language facet: the app prefixes every value with "Language_" (e.g.
      // "Language_English"). Strip it from the DISPLAY text only — the app applies
      // the filter from its own internal state, not this text, so filtering is
      // unaffected. Values render lazily + can re-render, so watch the group.
      if (/^language\b/i.test(label)) {
        var stripLang = function () {
          var nm = group.querySelectorAll('.cloud-search-filter-value__name');
          for (var i = 0; i < nm.length; i++) {
            var t = nm[i].textContent;
            if (/^\s*language[_\s]/i.test(t)) nm[i].textContent = t.replace(/^\s*language[_\s]+/i, '');
          }
        };
        var lmo = new MutationObserver(function () { lmo.disconnect(); stripLang(); lmo.observe(group, { childList: true, subtree: true }); });
        lmo.observe(group, { childList: true, subtree: true });
      }
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var wasOpen = fd.classList.contains('is-open');
        closeAllFD();
        if (wasOpen) return;
        /* Self-heal: the app sometimes re-renders its (hidden) sidebar, leaving
           our panel holding a stale/empty copy of this filter — that's the
           "dropdown opens but doesn't work" bug. Re-adopt the fresh group. */
        try {
          var mine = panel.querySelector('.cloud-search-filter');
          var fresh = null;
          [].forEach.call(sidebar.querySelectorAll('.cloud-search-filter'), function (g) {
            if (g === mine) return;
            var n = g.querySelector('.cloud-search-filter__name');
            var l = n ? n.textContent.replace(/\s+/g, ' ').trim().replace(/(:\s*)language[_\s]+/ig, '$1') : '';
            if (l === label) fresh = g;
          });
          if (fresh) {
            if (mine) mine.remove();
            panel.appendChild(fresh);
            nameEl = fresh.querySelector('.cloud-search-filter__name');
            if (/^language\b/i.test(label)) {
              [].forEach.call(fresh.querySelectorAll('.cloud-search-filter-value__name'), function (nm) {
                nm.textContent = nm.textContent.replace(/^\s*language[_\s]+/i, '');
              });
            }
          }
        } catch (err) {}
        /* expand → the app renders the values. Check the REAL state: an active
           facet is already expanded, and a blind click collapsed it (the
           "dropdown for an active filter opens empty" bug) */
        var grpNow = panel.querySelector('.cloud-search-filter');
        if (nameEl && !isExpanded(grpNow)) { try { nameEl.click(); } catch (err) {} }
        fd.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
        openFD = { fd: fd, btn: btn };
        panel.style.left = ''; panel.style.right = '';
        requestAnimationFrame(function () {
          var r = panel.getBoundingClientRect();
          if (r.right > window.innerWidth - 8) { panel.style.left = 'auto'; panel.style.right = '0'; }
        });
      });
      facetDropdowns.push(fd);
    });
    document.addEventListener('click', function (e) {
      if (!openFD || openFD.fd.contains(e.target)) return;
      if (e.target.closest && e.target.closest('.cloud-search-filter-values-container')) return; // the app's portalled value popover
      closeAllFD();
    });
    document.addEventListener('keydown', function (e) {
      if ((e.key === 'Escape' || e.key === 'Esc') && openFD) {
        var b = openFD.btn;
        closeAllFD();
        /* keep keyboard users on the pill — without scrolling: the theme's
           large scroll-padding-top made focus() jump the page ~400px */
        try { b.focus({ preventScroll: true }); } catch (err) {}
      }
    });
    /* "Language_English" → "English" wherever the app renders values —
       including the search page's popovers portalled to <body> */
    if (window.MutationObserver) {
      var langT = null;
      new MutationObserver(function () {
        if (langT) return;
        langT = setTimeout(function () {
          langT = null;
          [].forEach.call(document.querySelectorAll('.cloud-search-filter-value__name'), function (nm) {
            var t = nm.textContent;
            if (/^\s*language[_\s]/i.test(t)) nm.textContent = t.replace(/^\s*language[_\s]+/i, '');
          });
        }, 80);
      }).observe(document.body, { childList: true, subtree: true });
    }

    /* Count */
    var n = document.querySelectorAll('#product-grid .product-card').length;
    var count = document.createElement('span'); count.className = 'derpy-tb-count'; if (n) count.textContent = n + ' shown';

    /* LEFT — relocate the breadcrumb into the toolbar so the page-path and the
       filters share one row (the original section is hidden via CSS). */
    var crumbSlot = document.createElement('div'); crumbSlot.className = 'derpy-tb-crumb';
    if (IS_SEARCH) {
      var cs = document.createElement('span'); cs.className = 'derpy-tb-crumb__search';
      cs.appendChild(document.createTextNode('Results for '));
      var cst = document.createElement('strong'); cst.textContent = '“' + (p.get('q') || '') + '”';
      cs.appendChild(cst);
      crumbSlot.appendChild(cs);
    } else if (crumbNav) { crumbSlot.appendChild(crumbNav); }

    /* CENTER — the filter controls, kept centered in the toolbar grid. */
    var center = document.createElement('div'); center.className = 'derpy-tb-center';
    center.appendChild(sform); center.appendChild(stockBtn); center.appendChild(sortWrap);
    facetDropdowns.forEach(function (fd) { center.appendChild(fd); });

    /* RIGHT — result count */
    var right = document.createElement('div'); right.className = 'derpy-tb-right';
    right.appendChild(count);

    bar.appendChild(crumbSlot);
    bar.appendChild(center);
    bar.appendChild(right);

    /* Active filter chips */
    var chipsRow = document.createElement('div'); chipsRow.className = 'derpy-tb-chips';
    var chips = [];
    p.forEach(function (v, k) {
      if (k === 'filter_search') chips.push({ k: k, label: '“' + v + '”' });
      // Any active facet param (filter_set_name, filter_rarity_filter, filter_language, …)
      // gets a removable chip. Availability is the in-stock toggle; search has its own chip.
      else if (/^filter_price\[/.test(k)) {
        if (/^\d+(\.\d+)?$/.test(v)) chips.push({ k: k, label: prettyName(k) + ' $' + v });
      }
      else if (k.indexOf('filter_') === 0 && k.indexOf('filter_availability') !== 0) {
        var dv = v.replace(/^\s*language[_\s]+/i, ''); // drop the "Language_" prefix in chips too
        chips.push({ k: k, label: prettyName(k) + ': ' + dv });
      }
    });
    chips.forEach(function (c) {
      var chip = document.createElement('button'); chip.type = 'button'; chip.className = 'derpy-tb-chip';
      /* chip labels come straight from the URL — text only, never HTML (a
         crafted ?filter_search=<img onerror=…> link executed script here) */
      var chipText = document.createElement('span'); chipText.textContent = c.label;
      chip.appendChild(chipText);
      chip.insertAdjacentHTML('beforeend', IC.x);
      chip.addEventListener('click', function () { var np = qp(); np.delete(c.k); navTo(np); });
      chipsRow.appendChild(chip);
    });
    if (chips.length) {
      var clearAll = document.createElement('button'); clearAll.type = 'button'; clearAll.className = 'derpy-tb-clear'; clearAll.textContent = 'Clear all';
      clearAll.addEventListener('click', function () {
        if (IS_SEARCH) {
          var sp = new URLSearchParams(); var cq = qp();
          if (cq.get('q')) sp.set('q', cq.get('q'));
          if (cq.get('sort_by')) sp.set('sort_by', cq.get('sort_by'));
          navTo(sp);
        } else navTo(new URLSearchParams('sort_by=' + DEFAULT_SORT + '&' + IN_STOCK_KEY + '=true'));
      });
      chipsRow.appendChild(clearAll);
    }

    if (IS_SEARCH) {
      /* mount directly above the grid, inside its column, so position:sticky
         spans the whole results list */
      products.insertBefore(bar, grid);
      document.body.classList.add('derpy-app-search');
    } else {
      products.insertBefore(bar, products.firstChild);
    }
    if (chips.length) products.insertBefore(chipsRow, bar.nextSibling);
    document.body.classList.add('derpy-filters-ready');
    setHeaderHeight();
    setTimeout(setHeaderHeight, 300);
    forceRevealGrid();
    killRevealTriggers();
  }

  /* The Cloud Search app injects product cards AFTER the theme's ScrollTrigger
     scroll-reveal has already run on load, so late-injected / below-the-fold
     cards can be left in the animation's initial hidden state (opacity:0,
     scale) — which looked like "missing cards at the end of the gallery" on
     taller/narrower layouts. Force every card image to its revealed state with
     inline !important (beats both the CSS initial state and any GSAP inline). */
  function forceRevealGrid() {
    try {
      var els = document.querySelectorAll('#product-grid .product-card--image-primary, #product-grid .product-card--image, .products .product-card--image-primary');
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        if (el.getAttribute('data-derpy-revealed')) continue; // stamp once, no per-tick churn
        var s = el.style;
        s.setProperty('opacity', '1', 'important');
        s.setProperty('visibility', 'visible', 'important');
        s.setProperty('transform', 'none', 'important');
        el.setAttribute('data-derpy-revealed', '1');
      }
    } catch (e) {}
  }

  /* The theme's scroll-reveal ScrollTriggers keep doing per-scroll work on
     cards we've already force-revealed — dead weight that made scrolling feel
     rough/stuttery (and the old ScrollTrigger.refresh() polling here was worse:
     ~100 full recalcs in the first seconds = "page is slowing down" warnings).
     Kill just the card-image reveal triggers; nothing else is touched. */
  function killRevealTriggers() {
    try {
      if (!window.ScrollTrigger) return;
      window.ScrollTrigger.getAll().forEach(function (st) {
        var el = st.trigger || (st.vars && st.vars.trigger);
        if (el && el.matches && el.matches('.product-card--image-primary, .collection-card--link')) st.kill();
      });
    } catch (e) {}
  }

  function setHeaderHeight() {
    var h = document.querySelector('sticky-header, theme-header, #header');
    if (h) {
      var hh = Math.round(h.getBoundingClientRect().height);
      if (hh > 0) document.documentElement.style.setProperty('--derpy-header-h', hh + 'px');
    }
    var tb = document.querySelector('.derpy-toolbar');
    if (tb) {
      var th = Math.round(tb.getBoundingClientRect().height);
      if (th > 0) document.documentElement.style.setProperty('--derpy-toolbar-h', th + 'px');
    }
  }

  function start() {
    setHeaderHeight();
    setTimeout(setHeaderHeight, 600);
    window.addEventListener('resize', setHeaderHeight);
    var tries = 0;
    var iv = setInterval(function () { tries++; build(); if (built || tries > 60) clearInterval(iv); }, 200);
    /* Reveal safety net for cards the app injects late: a short, cheap loop
       (stamp-once, NO ScrollTrigger.refresh), then stop. */
    forceRevealGrid();
    var rTries = 0;
    var rv = setInterval(function () {
      rTries++;
      forceRevealGrid();
      if (rTries === 6) killRevealTriggers();
      if (rTries > 12) clearInterval(rv);
    }, 500);
    window.addEventListener('load', function () { forceRevealGrid(); killRevealTriggers(); });
    document.addEventListener('theme:reload_animations', function () {
      setTimeout(function () { forceRevealGrid(); killRevealTriggers(); }, 50);
    });
  }
  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
})();


/*
 * Native /search: the SAME pill toolbar the collection galleries use (search
 * box · In stock · Sort · one dropdown per filter · chips · count), built from
 * Shopify's own search filters. Store-wide search can't go through the Cloud
 * Search app — its /a/search/all pages ignore filters server-side and it only
 * matches titles — so search keeps Shopify's relevance ranking and gains the
 * site's filter UI instead of the theme's drawer (which only exists on phones;
 * at tablet/desktop widths the theme's sidebar is hidden and search had NO
 * filters). Filters apply by navigation, exactly like the collection toolbar.
 */
(function () {
  'use strict';
  if (!document.body.classList.contains('template-search')) return;
  if (location.pathname.indexOf('/a/search') === 0) return; // the app's search page gets the gallery toolbar instead
  var form = document.getElementById('FacetFiltersForm') || document.getElementById('FacetFiltersFormMobile');
  var grid = document.getElementById('product-grid');
  if (!form || !grid) return; // no results / no filters → nothing to build

  var IC = {
    search: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></svg>',
    x: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
    chev: '<svg class="derpy-fd__chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"></polyline></svg>'
  };
  var AVAIL = 'filter.v.availability';
  var PRICE_MIN = 'filter.v.price.gte', PRICE_MAX = 'filter.v.price.lte';
  var SORT_LABELS = { 'relevance': 'Relevance', 'price-ascending': 'Price: Low to High', 'price-descending': 'Price: High to Low' };

  function qp() { return new URLSearchParams(location.search); }
  function navTo(p) { p.delete('page'); var qs = p.toString(); location.assign('/search' + (qs ? '?' + qs : '')); }
  function clean(t) { return (t || '').replace(/\s+/g, ' ').trim(); }
  /* escapes for text AND attribute context — URL values land inside value="…" */
  function esc(t) {
    return String(t == null ? '' : t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function money(v) {
    var s = String(v == null ? '' : v).trim();
    if (!/^\d+(\.\d+)?$/.test(s)) return null;
    return '$' + parseFloat(s).toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  /* ---- read the native filters ---- */
  var facets = [];          // { label, name, type: 'list'|'price', options: [{value,label,checked,disabled}] , max }
  var valueLabels = {};     // name + '=' + value → display label (for chips)
  [].forEach.call(form.querySelectorAll('details'), function (d) {
    var s = d.querySelector('summary');
    var label = clean(s ? s.textContent : '');
    if (!label) return;
    var price = d.querySelector('input[name="' + PRICE_MIN + '"]');
    if (price) {
      var maxIn = d.querySelector('input[name="' + PRICE_MAX + '"]');
      facets.push({ label: label, type: 'price', max: maxIn ? maxIn.getAttribute('placeholder') : '' });
      return;
    }
    var boxes = d.querySelectorAll('input[type="checkbox"][name^="filter."]');
    if (!boxes.length) return;
    var f = { label: label, name: boxes[0].name, type: 'list', options: [] };
    [].forEach.call(boxes, function (b) {
      var l = b.id ? form.querySelector('label[for="' + b.id + '"]') : null;
      var text = clean(l ? l.textContent : b.value);
      f.options.push({ value: b.value, label: text, checked: b.checked, disabled: b.disabled });
      valueLabels[b.name + '=' + b.value] = text;
    });
    facets.push(f);
  });
  if (!facets.length) return;

  var p = qp();
  var bar = document.createElement('div');
  bar.className = 'derpy-toolbar derpy-toolbar--search';

  /* LEFT — what was searched (desktop only; hidden on phones by the shared CSS) */
  var crumb = document.createElement('div'); crumb.className = 'derpy-tb-crumb';
  var term = clean(p.get('q') || '');
  crumb.innerHTML = '<span class="derpy-tb-crumb__search">Results for <strong>“' + esc(term) + '”</strong></span>';

  /* search box — edits the store-wide query */
  var sform = document.createElement('form'); sform.className = 'derpy-tb-search'; sform.setAttribute('role', 'search');
  sform.innerHTML = IC.search;
  var sinput = document.createElement('input'); sinput.type = 'search'; sinput.className = 'derpy-tb-search__input';
  sinput.placeholder = 'Search all cards…'; sinput.value = term; sinput.setAttribute('aria-label', 'Search the store');
  sform.appendChild(sinput);
  sform.addEventListener('submit', function (e) {
    e.preventDefault();
    var np = qp(); var v = sinput.value.trim();
    if (!v) return;
    np.set('q', v);
    navTo(np);
  });

  var center = document.createElement('div'); center.className = 'derpy-tb-center';
  center.appendChild(sform);

  /* In stock toggle — maps to Shopify's availability filter */
  var availFacet = null;
  facets = facets.filter(function (f) {
    if (f.type === 'list' && f.name === AVAIL) { availFacet = f; return false; }
    return true;
  });
  if (availFacet) {
    var inStock = p.getAll(AVAIL).indexOf('1') > -1;
    var stockBtn = document.createElement('button'); stockBtn.type = 'button';
    stockBtn.className = 'derpy-tb-toggle' + (inStock ? ' is-on' : '');
    stockBtn.setAttribute('aria-pressed', inStock ? 'true' : 'false');
    stockBtn.innerHTML = '<span class="derpy-tb-toggle__track"><span class="derpy-tb-toggle__dot"></span></span><span>In stock</span>';
    stockBtn.addEventListener('click', function () {
      var np = qp();
      np.delete(AVAIL);
      if (!inStock) np.append(AVAIL, '1');
      navTo(np);
    });
    center.appendChild(stockBtn);
  }

  /* Sort — the native options (Relevance default) */
  var nativeSort = form.querySelector('select[name="sort_by"]') || document.querySelector('select[name="sort_by"]');
  var sortVals = nativeSort ? [].map.call(nativeSort.options, function (o) { return [o.value, SORT_LABELS[o.value] || clean(o.textContent)]; }) : [['relevance', 'Relevance']];
  var curSort = p.get('sort_by') || 'relevance';
  var sortWrap = document.createElement('div'); sortWrap.className = 'derpy-tb-sort';
  sortWrap.innerHTML = '<span class="derpy-tb-sort__label">Sort</span><span class="derpy-tb-select"><select aria-label="Sort by">' +
    sortVals.map(function (o) { return '<option value="' + esc(o[0]) + '"' + (o[0] === curSort ? ' selected' : '') + '>' + esc(o[1]) + '</option>'; }).join('') +
    '</select></span>';
  sortWrap.querySelector('select').addEventListener('change', function () {
    var np = qp();
    if (this.value === 'relevance') np.delete('sort_by'); else np.set('sort_by', this.value);
    navTo(np);
  });
  center.appendChild(sortWrap);

  /* One dropdown per filter */
  var openFD = null;
  function closeAllFD() {
    if (openFD) { openFD.fd.classList.remove('is-open'); openFD.btn.setAttribute('aria-expanded', 'false'); openFD = null; }
  }
  facets.forEach(function (f) {
    var fd = document.createElement('div'); fd.className = 'derpy-fd';
    var active = f.type === 'price' ? (money(p.get(PRICE_MIN)) || money(p.get(PRICE_MAX)) ? 1 : 0)
      : f.options.filter(function (o) { return o.checked; }).length;
    var btn = document.createElement('button'); btn.type = 'button'; btn.className = 'derpy-fd__btn';
    btn.setAttribute('aria-haspopup', 'true'); btn.setAttribute('aria-expanded', 'false');
    btn.innerHTML = '<span>' + esc(f.label) + '</span>' + (active ? '<span class="derpy-fd__badge">' + active + '</span>' : '') + IC.chev;
    var panel = document.createElement('div'); panel.className = 'derpy-fd__panel';

    if (f.type === 'price') {
      panel.innerHTML =
        '<form class="derpy-nf-price">' +
          '<label><span>Min</span><input type="number" inputmode="decimal" min="0" step="any" name="min" placeholder="0" value="' + esc(p.get(PRICE_MIN) || '') + '"></label>' +
          '<span class="derpy-nf-price__dash">–</span>' +
          '<label><span>Max</span><input type="number" inputmode="decimal" min="0" step="any" name="max" placeholder="' + esc(String(f.max || '').replace(/,/g, '')) + '" value="' + esc(p.get(PRICE_MAX) || '') + '"></label>' +
          '<button type="submit" class="derpy-nf-apply">Apply</button>' +
        '</form>';
      panel.querySelector('form').addEventListener('submit', function (e) {
        e.preventDefault();
        var np = qp();
        var lo = this.elements.min.value.trim(), hi = this.elements.max.value.trim();
        np.delete(PRICE_MIN); np.delete(PRICE_MAX);
        if (lo) np.set(PRICE_MIN, lo);
        if (hi) np.set(PRICE_MAX, hi);
        navTo(np);
      });
    } else {
      var list = document.createElement('div'); list.className = 'derpy-nf-list';
      f.options.forEach(function (o) {
        var row = document.createElement('label');
        row.className = 'derpy-nf-opt' + (o.disabled && !o.checked ? ' is-disabled' : '');
        row.innerHTML = '<input type="checkbox"' + (o.checked ? ' checked' : '') + (o.disabled && !o.checked ? ' disabled' : '') + '><span>' + esc(o.label) + '</span>';
        row.querySelector('input').addEventListener('change', function () {
          var np = qp();
          var vals = np.getAll(f.name).filter(function (v) { return v !== o.value; });
          if (this.checked) vals.push(o.value);
          np.delete(f.name);
          vals.forEach(function (v) { np.append(f.name, v); });
          navTo(np);
        });
        list.appendChild(row);
      });
      panel.appendChild(list);
    }

    fd.appendChild(btn); fd.appendChild(panel);
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      var wasOpen = fd.classList.contains('is-open');
      closeAllFD();
      if (wasOpen) return;
      fd.classList.add('is-open'); btn.setAttribute('aria-expanded', 'true');
      openFD = { fd: fd, btn: btn };
      panel.style.left = ''; panel.style.right = '';
      var r = panel.getBoundingClientRect();
      if (r.right > window.innerWidth - 8) { panel.style.left = 'auto'; panel.style.right = '0'; }
    });
    center.appendChild(fd);
  });
  document.addEventListener('click', function (e) { if (openFD && !openFD.fd.contains(e.target)) closeAllFD(); });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape' || e.key === 'Esc') closeAllFD(); });

  /* RIGHT — total results (the theme prints "N results") */
  var right = document.createElement('div'); right.className = 'derpy-tb-right';
  var total = (function () {
    var m = /(\d[\d,]*)\s+results?/i.exec(document.title) || /(\d[\d,]*)\s+results?/i.exec((document.querySelector('.facets--bar-right') || {}).textContent || '');
    return m ? m[1] : '';
  })();
  if (total) {
    var count = document.createElement('span'); count.className = 'derpy-tb-count'; count.setAttribute('data-total', total);
    count.textContent = total + (total === '1' ? ' result' : ' results'); right.appendChild(count);
  }

  bar.appendChild(crumb); bar.appendChild(center); bar.appendChild(right);

  /* Active filter chips */
  var chipsRow = document.createElement('div'); chipsRow.className = 'derpy-tb-chips';
  var labelOf = {}; facets.forEach(function (f) { if (f.name) labelOf[f.name] = f.label; });
  function addChip(text, remove) {
    var chip = document.createElement('button'); chip.type = 'button'; chip.className = 'derpy-tb-chip';
    chip.innerHTML = '<span>' + esc(text) + '</span>' + IC.x;
    chip.addEventListener('click', function () { var np = qp(); remove(np); navTo(np); });
    chipsRow.appendChild(chip);
  }
  var seenKey = {};
  p.forEach(function (v, k) {
    if (k.indexOf('filter.') !== 0 || k === AVAIL || k === PRICE_MIN || k === PRICE_MAX) return;
    if (seenKey[k + '=' + v]) return; seenKey[k + '=' + v] = 1;
    addChip((labelOf[k] ? labelOf[k] + ': ' : '') + (valueLabels[k + '=' + v] || v), function (np) {
      var keep = np.getAll(k).filter(function (x) { return x !== v; });
      np.delete(k); keep.forEach(function (x) { np.append(k, x); });
    });
  });
  var loM = money(p.get(PRICE_MIN)), hiM = money(p.get(PRICE_MAX));
  if (loM || hiM) {
    addChip('Price: ' + (loM || '$0') + ' – ' + (hiM || 'any'), function (np) { np.delete(PRICE_MIN); np.delete(PRICE_MAX); });
  }
  if (chipsRow.children.length) {
    var clearAll = document.createElement('button'); clearAll.type = 'button'; clearAll.className = 'derpy-tb-clear'; clearAll.textContent = 'Clear all';
    clearAll.addEventListener('click', function () {
      var np = new URLSearchParams();
      var q = qp().get('q'); if (q) np.set('q', q);
      var s = qp().get('sort_by'); if (s) np.set('sort_by', s);
      navTo(np);
    });
    chipsRow.appendChild(clearAll);
  }

  /* mount directly above the grid (inside the grid's own column, so the
     toolbar's position:sticky spans the whole results list) */
  grid.parentNode.insertBefore(bar, grid);
  if (chipsRow.children.length) grid.parentNode.insertBefore(chipsRow, grid);
  document.body.classList.add('derpy-search-tb');
  window.dispatchEvent(new Event('resize')); // re-measure --derpy-toolbar-h
})();


/*
 * Page titles + accessible drawer close buttons.
 * - /a/search pages (filter-app proxy) have no page_title, so every gallery
 *   and search tab was just "Derpy Cards". Name them after the category / query.
 * - The theme's <side-panel-close> (quick view, cart, filter drawers) is an
 *   unlabeled element that keyboards can't reach and screen readers skip.
 */
(function () {
  'use strict';
  try {
    var path = location.pathname;
    if (/^\/a\/search(\/|$)/.test(path) && /^derpy cards$/i.test(document.title.trim())) {
      var q = new URLSearchParams(location.search).get('q');
      var m = /^\/a\/search\/([^/?#]+)/.exec(path);
      if (!m && q) document.title = 'Search: “' + q + '” – Derpy Cards';
      else if (m) {
        var crumbs = document.querySelectorAll('.breadcrumbs a');
        var name = crumbs.length ? crumbs[crumbs.length - 1].textContent.replace(/\s+/g, ' ').trim() : '';
        if (!name || /^home$/i.test(name)) name = decodeURIComponent(m[1]).replace(/[-_]+/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
        document.title = name + ' – Derpy Cards';
      }
    }
  } catch (e) {}

  function labelCloses() {
    [].forEach.call(document.querySelectorAll('side-panel-close:not([data-derpy-a11y]), .side-panel-close:not([data-derpy-a11y])'), function (b) {
      b.setAttribute('data-derpy-a11y', '1');
      if (!b.getAttribute('aria-label')) b.setAttribute('aria-label', 'Close');
      if (b.tagName !== 'BUTTON') { b.setAttribute('role', 'button'); if (!b.hasAttribute('tabindex')) b.setAttribute('tabindex', '0'); }
      var svg = b.querySelector('svg'); if (svg) svg.setAttribute('aria-hidden', 'true');
    });
    /* other icon-only theme controls with no accessible name */
    [].forEach.call(document.querySelectorAll('summary.mobile-toggle:not([aria-label])'), function (s) { s.setAttribute('aria-label', 'Menu'); });
    var cartLink = document.getElementById('cart-drawer-toggle');
    if (cartLink) { // re-rendered after every cart change — keep the count current
      var n = parseInt(((cartLink.querySelector('.thb-item-count') || {}).textContent || '0').trim(), 10) || 0;
      var lbl = 'Cart, ' + n + (n === 1 ? ' item' : ' items');
      if (cartLink.getAttribute('aria-label') !== lbl) cartLink.setAttribute('aria-label', lbl);
    }
    [].forEach.call(document.querySelectorAll('a.remove:not([aria-label])'), function (a) {
      var row = a.closest('.product-card-small, .product-cart-item, .cart-item, tr, li');
      var t = row && row.querySelector('a[href*="/products/"]:not(.remove)');
      a.setAttribute('aria-label', 'Remove ' + (t ? t.textContent.replace(/\s+/g, ' ').trim() : 'item'));
    });
    [].forEach.call(document.querySelectorAll('.gallery-item--content-link:not([aria-label])'), function (a) {
      var item = a.closest('.gallery-item');
      var h = item && item.querySelector('h1, h2, h3, h4, .gallery-item--content-title, .button');
      var text = h ? h.textContent.replace(/\s+/g, ' ').trim() : '';
      if (!text) { var img = item && item.querySelector('img[alt]:not([alt=""])'); text = img ? img.getAttribute('alt') : ''; }
      if (!text) { // last resort: name the destination ("Pokemon Tcg", "Graded Slabs")
        var seg = (a.getAttribute('href') || '').split('?')[0].split('/').filter(Boolean).pop() || '';
        text = decodeURIComponent(seg).replace(/[-_]+/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
      }
      if (text) a.setAttribute('aria-label', text);
    });
    /* footer newsletter: the theme's hand-rolled pattern rejects valid emails
       (plus-addressing, TLDs longer than 4 letters) — type=email validates */
    [].forEach.call(document.querySelectorAll('.signup-form input[type="email"][pattern]'), function (i) { i.removeAttribute('pattern'); });
    /* the importer named the singles' option "Title" (Shopify's placeholder):
       show "Condition" on the product page and in the cart */
    [].forEach.call(document.querySelectorAll('variant-selects .form__label, .product-form .form__label, .product-add-to-cart-sticky .form__label, .product-card-small--options > span'), function (el) {
      var first = el.firstChild;
      if (first && first.nodeType === 3 && /^\s*Title\s*$/.test(first.textContent)) first.textContent = first.textContent.replace('Title', 'Condition');
    });
  }

  /* PDP: clicking the main image did nothing — only a small hover-only zoom
     icon opened the viewer. Make the image itself open it. */
  document.addEventListener('click', function (e) {
    var img = e.target.closest && e.target.closest('.thb-product-detail product-slider .product-single__media-image img');
    if (!img || img.closest('.product-quick-images')) return;
    var zoom = img.closest('.product-single__media');
    zoom = zoom && zoom.querySelector('.product-single__media-zoom');
    if (zoom) { e.preventDefault(); zoom.click(); }
  }, true);

  /* Homepage collection tabs: the "Shop …" button never followed the chosen
     tab — the theme looks for its links inside <collection-tabs>, but they
     render outside it (collection-tabs.js line 17). Mirror the active tab. */
  document.addEventListener('click', function (e) {
    var tabBtn = e.target.closest && e.target.closest('collection-tabs button');
    if (!tabBtn) return;
    var ct = tabBtn.closest('collection-tabs');
    var section = ct.closest('.collection-tabs') || ct.closest('.shopify-section');
    if (!section) return;
    var idx = [].indexOf.call(ct.querySelectorAll('button'), tabBtn);
    var links = section.querySelectorAll('.linked-to-tab');
    if (idx < 0 || idx >= links.length) return;
    [].forEach.call(links, function (l, i) { l.classList.toggle('active', i === idx); });
  });
  /* the theme's close element only listens for click — make Enter/Space work */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var c = e.target && e.target.closest && e.target.closest('side-panel-close, .side-panel-close');
    if (c && c.getAttribute('role') === 'button') { e.preventDefault(); c.click(); }
  });
  function start() {
    labelCloses();
    if (window.MutationObserver) {
      var t = null; // drawers re-render their contents (cart updates, quick views)
      new MutationObserver(function () {
        if (t) return;
        t = setTimeout(function () { t = null; labelCloses(); }, 120);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }
  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
})();


/*
 * Fix: the "Yu-Gi-Oh!" top-nav item has no URL set in the menu, so it isn't
 * clickable (every other category links to its collection). Set its href to
 * the Yu-Gi-Oh collection. Theme-side only — does not touch the live menu config.
 */
(function () {
  'use strict';
  function fixYgo() {
    /* footer too — its "Yu-Gi-Oh!" menu items are href="#" as well */
    var links = document.querySelectorAll('.full-menu a, .header-menu a, nav a, footer a, .shopify-section-group-footer-group a, .mobile-menu-drawer a');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      var t = (a.textContent || '').replace(/\s+/g, ' ').trim();
      if (!/^yu\-?gi\-?oh/i.test(t)) continue;
      var href = a.getAttribute('href');
      if (!href || href === '#' || href.trim() === '') {
        a.setAttribute('href', '/collections/yu-gi-oh-1');
      }
    }
  }
  if (document.readyState !== 'loading') fixYgo();
  else document.addEventListener('DOMContentLoaded', fixYgo);
})();


/*
 * Quick-view image zoom lightbox: click a product image inside the quick-view
 * slide-out to open it fullscreen with zoom in/out (+/- buttons, scroll wheel,
 * double-click) and drag-to-pan, so cards can be inspected super up close.
 * Requests a high-res (width=2048) version of the image for sharp zooming.
 */
(function () {
  'use strict';
  var overlay, stage, img, levelEl, countEl;
  var images = [], curIndex = 0;
  var scale = 1, minScale = 1, maxScale = 6, tx = 0, ty = 0;
  var panning = false, startX = 0, startY = 0, baseTx = 0, baseTy = 0;

  function build() {
    if (overlay) return;
    overlay = document.createElement('div');
    overlay.className = 'derpy-zoom-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-label', 'Image viewer');
    overlay.innerHTML =
      '<div class="derpy-zoom-hint">Scroll or +/− to zoom · drag to pan · ← → for images</div>' +
      '<button class="derpy-zoom-close" type="button" aria-label="Close">×</button>' +
      '<button class="derpy-zoom-nav derpy-zoom-nav--prev" type="button" aria-label="Previous image">‹</button>' +
      '<button class="derpy-zoom-nav derpy-zoom-nav--next" type="button" aria-label="Next image">›</button>' +
      '<div class="derpy-zoom-stage"><img class="derpy-zoom-img" alt=""></div>' +
      '<div class="derpy-zoom-count"></div>' +
      '<div class="derpy-zoom-controls">' +
        '<button class="derpy-zoom-btn" type="button" data-z="out" aria-label="Zoom out">−</button>' +
        '<span class="derpy-zoom-level">100%</span>' +
        '<button class="derpy-zoom-btn" type="button" data-z="in" aria-label="Zoom in">+</button>' +
      '</div>';
    document.body.appendChild(overlay);
    stage = overlay.querySelector('.derpy-zoom-stage');
    img = overlay.querySelector('.derpy-zoom-img');
    levelEl = overlay.querySelector('.derpy-zoom-level');
    countEl = overlay.querySelector('.derpy-zoom-count');

    overlay.querySelector('.derpy-zoom-close').addEventListener('click', close);
    overlay.addEventListener('click', function (e) { if (e.target === overlay || e.target === stage) close(); });
    overlay.querySelector('.derpy-zoom-nav--prev').addEventListener('click', function (e) { e.stopPropagation(); navStep(-1); });
    overlay.querySelector('.derpy-zoom-nav--next').addEventListener('click', function (e) { e.stopPropagation(); navStep(1); });
    [].forEach.call(overlay.querySelectorAll('.derpy-zoom-btn'), function (b) {
      b.addEventListener('click', function () { zoomBy(b.getAttribute('data-z') === 'in' ? 1.4 : 1 / 1.4); });
    });
    stage.addEventListener('wheel', function (e) { e.preventDefault(); zoomAt(e.deltaY < 0 ? 1.18 : 1 / 1.18, e.clientX, e.clientY); }, { passive: false });
    stage.addEventListener('pointerdown', function (e) {
      if (scale <= minScale) return;
      panning = true; stage.classList.add('is-panning');
      startX = e.clientX; startY = e.clientY; baseTx = tx; baseTy = ty;
      try { stage.setPointerCapture(e.pointerId); } catch (err) {}
    });
    stage.addEventListener('pointermove', function (e) {
      if (!panning) return; tx = baseTx + (e.clientX - startX); ty = baseTy + (e.clientY - startY); apply();
    });
    function endPan() { panning = false; stage.classList.remove('is-panning'); }
    stage.addEventListener('pointerup', endPan);
    stage.addEventListener('pointercancel', endPan);
    img.addEventListener('dblclick', function (e) { zoomAt(scale > minScale ? (minScale / scale) : 2.5, e.clientX, e.clientY); });
    /* The theme closes EVERY open side panel on keyup Escape (app.js), so the
       Esc that closes the zoom would also close the quick view underneath.
       Swallow that one keyup before it reaches the theme. */
    var zoomEscAt = 0;
    window.addEventListener('keyup', function (e) {
      if ((e.key === 'Escape' || e.key === 'Esc') && Date.now() - zoomEscAt < 1500) {
        zoomEscAt = 0; e.stopImmediatePropagation();
      }
    }, true);
    document.addEventListener('keydown', function (e) {
      if (!overlay.classList.contains('is-open')) return;
      if (e.key === 'Escape') { zoomEscAt = Date.now(); close(); }
      else if (e.key === '+' || e.key === '=') zoomBy(1.4);
      else if (e.key === '-' || e.key === '_') zoomBy(1 / 1.4);
      else if (e.key === 'ArrowLeft') navStep(-1);
      else if (e.key === 'ArrowRight') navStep(1);
    });
  }

  function apply() {
    if (scale <= minScale) { tx = 0; ty = 0; }
    img.style.transform = 'translate(calc(-50% + ' + tx + 'px), calc(-50% + ' + ty + 'px)) scale(' + scale + ')';
    levelEl.textContent = Math.round(scale * 100) + '%';
    stage.style.cursor = scale > minScale ? 'grab' : 'zoom-out';
  }
  function zoomBy(f) { scale = Math.min(maxScale, Math.max(minScale, scale * f)); apply(); }
  function zoomAt(f, cx, cy) {
    var prev = scale;
    scale = Math.min(maxScale, Math.max(minScale, scale * f));
    var r = stage.getBoundingClientRect();
    var ox = cx - r.left - r.width / 2, oy = cy - r.top - r.height / 2;
    var ratio = scale / prev;
    tx = (tx - ox) * ratio + ox; ty = (ty - oy) * ratio + oy;
    apply();
  }
  function hires(src) { try { var u = new URL(src, location.href); if (/cdn\.shopify\.com|\/cdn\//.test(u.href)) { u.searchParams.set('width', '2048'); } return u.href; } catch (e) { return src; } }
  function showImage(i) {
    if (!images.length) return;
    curIndex = (i % images.length + images.length) % images.length;
    scale = 1; tx = 0; ty = 0;
    img.src = hires(images[curIndex]); apply();
    if (countEl) countEl.textContent = (curIndex + 1) + ' / ' + images.length;
    overlay.classList.toggle('is-single', images.length <= 1);
  }
  function navStep(d) { if (images.length > 1) showImage(curIndex + d); }
  function open(list, index) {
    build();
    images = (list && list.length) ? list : [];
    document.body.classList.add('derpy-zoom-lock');
    showImage(index || 0);
    requestAnimationFrame(function () { overlay.classList.add('is-open'); });
  }
  function close() { if (overlay) { overlay.classList.remove('is-open'); document.body.classList.remove('derpy-zoom-lock'); } }

  document.addEventListener('click', function (e) {
    var im = e.target.closest && e.target.closest('#Product-Drawer .product-quick-images img, .thb-product-detail .product-quick-images img');
    if (!im) return;
    e.preventDefault(); e.stopPropagation();
    var clicked = im.currentSrc || im.src;
    // gather every image in this product's quick-view gallery (dedupe Flickity clones)
    var gallery = im.closest('.product-quick-images') || document;
    var seen = {}, list = [];
    [].forEach.call(gallery.querySelectorAll('img'), function (g) {
      var s = g.currentSrc || g.src;
      if (s && !seen[s]) { seen[s] = 1; list.push(s); }
    });
    if (!list.length) list = [clicked];
    var idx = list.indexOf(clicked); if (idx < 0) idx = 0;
    open(list, idx);
  }, true);
})();


/*
 * #6 — "Blogs" nav: rename to "News", point it at /blogs/news, and remove its
 * dropdown (the consolidated content lives in the News blog). Theme-side only —
 * does not modify the live menu config.
 */
(function () {
  'use strict';
  function fixBlogs() {
    var tops = document.querySelectorAll('.full-menu > ul > li, .header-menu > ul > li, .full-menu li.menu-item-has-children');
    [].forEach.call(tops, function (li) {
      var a = li.querySelector(':scope > a');
      if (!a) return;
      var t = (a.textContent || '').replace(/\s+/g, ' ').trim();
      if (!/^blogs?$/i.test(t)) return;
      a.textContent = 'News';
      a.setAttribute('href', '/blogs/news');
      li.classList.remove('menu-item-has-children');
      var sub = li.querySelector('ul'); if (sub) sub.parentNode.removeChild(sub);
    });
  }
  if (document.readyState !== 'loading') fixBlogs();
  else document.addEventListener('DOMContentLoaded', fixBlogs);
})();


/*
 * Data hygiene: the card importer (Store Pass) leaves literal "null" values in
 * spec rows ("Form: null - Evolves from null"). Hide/strip those on product
 * pages AND inside the quick-view drawer. Lowercase-only matching keeps real
 * names like the Pokémon "Type: Null" intact.
 */
(function () {
  'use strict';
  function clean(root) {
    try {
      var rows = (root || document).querySelectorAll('.store-pass-custom-description .info-item');
      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        if (row.getAttribute('data-derpy-cleaned')) continue;
        row.setAttribute('data-derpy-cleaned', '1');
        var label = row.querySelector('label');
        var val = (row.textContent || '').replace(label ? label.textContent : '', '').trim();
        if (/^null\b/.test(val)) { row.style.display = 'none'; }
        else if (/\bnull\b/.test(val)) {
          row.innerHTML = row.innerHTML.replace(/\s*[-–—]?\s*(Evolves from\s+)?null\b/g, '');
        }
        /* attack costs arrive comma-joined with no spaces ("Lightning,Lightning,
           Colorless") — one unbreakable word that shoved the attack text off
           the side of the quick view on phones */
        [].forEach.call(row.querySelectorAll('.store-pass-attack-cost'), function (l) {
          if (/,\S/.test(l.textContent)) l.textContent = l.textContent.replace(/,\s*/g, ', ');
        });
      }
    } catch (e) {}
  }
  function start() {
    clean(document);
    var drawer = document.getElementById('Product-Drawer-Content');
    if (drawer && window.MutationObserver) {
      new MutationObserver(function () { clean(drawer); }).observe(drawer, { childList: true, subtree: true });
    }
  }
  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
})();


/*
 * Smart bars (mobile): scrolling down tucks the fixed header + filter toolbar
 * away for full-screen browsing; the moment the user scrolls up a little,
 * both slide back in. Desktop keeps them permanently sticky (unchanged).
 */
(function () {
  'use strict';
  if (!document.documentElement.classList.contains('derpy-grid-page')) return;
  var mq = window.matchMedia('(max-width: 768px), (max-height: 500px) and (pointer: coarse)') /* landscape phones too */;
  var lastY = window.scrollY || 0, ticking = false;
  function onScroll() {
    var y = window.scrollY || document.documentElement.scrollTop;
    var cls = document.documentElement.classList;
    if (!mq.matches) { cls.remove('derpy-bars-hide'); lastY = y; return; }
    var dy = y - lastY;
    if (y < 90) cls.remove('derpy-bars-hide');           // near top: always shown
    else if (dy > 8) cls.add('derpy-bars-hide');         // scrolling down: tuck away
    else if (dy < -8) cls.remove('derpy-bars-hide');     // any upward nudge: bring back
    lastY = y;
  }
  window.addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () { ticking = false; onScroll(); });
  }, { passive: true });
})();


/*
 * Header nav restructure (theme-side DOM, menu config untouched):
 *   [Sell Us Your Cards ★highlight] [Events] [Consignment] [Graded Slabs] …
 *   [TCG Accessories] [News]            [Join Discord →header icon zone]
 * - "Sell Us Your Cards" pulled out of Community, highlighted, placed first
 * - "Upcoming Events" renamed "Events" (dropdown stripped), before Consignment
 * - "Consignment" moved up to sit right of Events, before Graded Slabs
 * - "News" (→ /blogs/news) added beside TCG Accessories
 * - "Discord" pulled out of Community, standalone at the far right
 * - "Trade In" removed from the nav (page itself stays reachable by URL)
 * - "Community" hidden if the moves leave it empty
 * - hovering a dropdown-less item must NOT dim the page (the theme flags
 *   `menu-item-hover` on <theme-header> for every item, which fades in its
 *   megamenu backdrop — suppress it when the hovered item has no submenu)
 */
(function () {
  'use strict';
  function restructure() {
    var nav = document.querySelector('.full-menu > ul');
    if (!nav || nav.getAttribute('data-derpy-nav')) return;
    var tops = [].slice.call(nav.children);
    function topByText(re) {
      for (var i = 0; i < tops.length; i++) {
        var a = tops[i].querySelector(':scope > a');
        if (a && re.test(a.textContent.replace(/\s+/g, ' ').trim())) return tops[i];
      }
      return null;
    }
    var graded = topByText(/^graded slabs$/i);
    if (!graded) return;
    nav.setAttribute('data-derpy-nav', '1');

    function extract(re, hrefRe) {
      var links = nav.querySelectorAll('li li a');
      for (var i = 0; i < links.length; i++) {
        var t = links[i].textContent.replace(/\s+/g, ' ').trim();
        if (re.test(t) || (hrefRe && hrefRe.test(links[i].getAttribute('href') || ''))) {
          return links[i].closest('li');
        }
      }
      return null;
    }

    /* Trade In: drop from nav */
    var trade = extract(/^trade[\s-]?in/i, /trade-in/);
    if (trade) trade.parentNode.removeChild(trade);

    /* Sell Us Your Cards: highlighted, first */
    var sell = extract(/sell us your cards/i, /buylist/);
    if (sell) {
      sell.classList.add('derpy-nav-sell');
      nav.insertBefore(sell, graded);
    }

    /* Upcoming Events → "Events" (standalone — dropdown/megamenu stripped),
       before Graded Slabs; "Consignment" moved up to sit right beside it */
    var events = topByText(/^upcoming events$/i) || topByText(/^events$/i);
    if (events) {
      var ea = events.querySelector(':scope > a');
      if (ea) {
        for (var n = 0; n < ea.childNodes.length; n++) {
          if (ea.childNodes[n].nodeType === 3 && /events/i.test(ea.childNodes[n].textContent)) {
            ea.childNodes[n].textContent = 'Events';
          }
        }
        var chev = ea.querySelector('svg, [class*="chevron"], .icon');
        if (chev && chev.parentNode) chev.parentNode.removeChild(chev);
      }
      events.classList.remove('menu-item-has-children');
      events.classList.remove('menu-item-has-megamenu');
      [].forEach.call(events.querySelectorAll(':scope > ul, :scope > .sub-menu, :scope > [class*="megamenu"], :scope > [class*="mega-menu"]'), function (sm) {
        sm.parentNode.removeChild(sm);
      });
      nav.insertBefore(events, graded);
    }
    var consign = topByText(/^consignment$/i);
    if (consign) nav.insertBefore(consign, graded);

    /* "News" beside TCG Accessories (the admin menu no longer carries a
       Blogs item, so this is inserted outright, not renamed) */
    var acc = topByText(/^tcg accessories$/i);
    if (acc && !topByText(/^news$/i)) {
      var news = document.createElement('li');
      news.className = acc.className || '';
      news.setAttribute('role', 'menuitem');
      var accA = acc.querySelector(':scope > a');
      news.innerHTML = '<a class="' + ((accA && accA.className) || '') + '" href="/blogs/news">News</a>';
      nav.insertBefore(news, acc.nextSibling);
    }

    /* Trading Card Games → "Other TCG" (dropdown kept) */
    var tcg = topByText(/^trading card games$/i);
    if (tcg) {
      var ta = tcg.querySelector(':scope > a');
      if (ta) {
        for (var m = 0; m < ta.childNodes.length; m++) {
          if (ta.childNodes[m].nodeType === 3 && /trading card games/i.test(ta.childNodes[m].textContent)) {
            ta.childNodes[m].textContent = 'Other TCG';
          }
        }
      }
    }

    /* Discord: standalone at the very right — into the header icon zone
       (next to account/cart) so it can never wrap the nav to a second row */
    var discord = extract(/^discord$/i, /discord\.gg/);
    if (discord) {
      var da = discord.querySelector('a');
      var iconZone = document.querySelector('.thb-secondary-area.thb-header-right');
      if (da && iconZone) {
        da.textContent = 'Join Discord';
        da.classList.add('derpy-nav-discord');
        iconZone.insertBefore(da, iconZone.firstChild);
        discord.parentNode.removeChild(discord);
      } else {
        discord.classList.add('derpy-nav-discord');
        nav.appendChild(discord);
      }
    }

    /* Community: removed from the nav entirely */
    var community = topByText(/^community$/i);
    if (community) community.parentNode.removeChild(community);

    /* ---- Mobile drawer: mirror the desktop nav ----
       Top-level drawer items are either <a class="link-container"> (no submenu)
       or <details><summary class="parent-link">Label…</summary><ul class=
       "sub-menu">…</details>. Match each item by its LABEL in either form —
       searching only <a> elements missed every item that has a submenu, so
       "Upcoming Events", "Trading Card Games" and "Community" never changed. */
    var drawer = document.querySelector('.mobile-menu-drawer');
    var dRoot = drawer && drawer.querySelector('ul.mobile-menu');
    if (drawer && dRoot && !drawer.getAttribute('data-derpy-nav')) {
      drawer.setAttribute('data-derpy-nav', '1');
      var ownText = function (el) {
        var t = '';
        for (var i = 0; i < el.childNodes.length; i++) if (el.childNodes[i].nodeType === 3) t += el.childNodes[i].textContent;
        return (t.trim() ? t : el.textContent).replace(/\s+/g, ' ').trim();
      };
      var headOf = function (li) { return li.querySelector(':scope > details > summary, :scope > a'); };
      var dItem = function (re) {
        for (var i = 0; i < dRoot.children.length; i++) {
          var h = headOf(dRoot.children[i]);
          if (h && re.test(ownText(h))) return dRoot.children[i];
        }
        return null;
      };
      var dLink = function (re) { // any link anywhere in the drawer (for items nested in submenus)
        var links = drawer.querySelectorAll('a');
        for (var i = 0; i < links.length; i++) if (re.test(links[i].textContent.replace(/\s+/g, ' ').trim())) return links[i];
        return null;
      };
      var relabel = function (li, text) {
        var h = headOf(li); if (!h) return;
        for (var i = 0; i < h.childNodes.length; i++) {
          if (h.childNodes[i].nodeType === 3 && h.childNodes[i].textContent.trim()) { h.childNodes[i].textContent = text; return; }
        }
      };
      var plainItem = function (href, text, cls) {
        var li = document.createElement('li'); if (cls) li.className = cls;
        var a = document.createElement('a'); a.className = 'link-container'; a.href = href; a.textContent = text;
        li.appendChild(a); return li;
      };

      /* Sell Us + Discord sit inside Community — pull them out before it goes */
      var sellA = dLink(/sell us your cards/i);
      var dSell = sellA && sellA.closest('li');
      var discA = dLink(/^(join )?discord$/i);
      var dDiscord = discA && discA.closest('li');
      var tradeA = dLink(/^trade[\s-]?in/i);
      if (tradeA && tradeA.closest('li')) tradeA.closest('li').remove();

      if (dSell) { dSell.classList.add('derpy-nav-sell'); dRoot.insertBefore(dSell, dRoot.firstChild); }

      /* Events: one plain link, same destination as the desktop item */
      var deskEv = [].find.call(document.querySelectorAll('.full-menu > ul > li > a'), function (a) { return /^(upcoming )?events$/i.test(a.textContent.replace(/\s+/g, ' ').trim()); });
      var evHref = (deskEv && deskEv.getAttribute('href')) || '/collections/events';
      var dEvents = dItem(/^upcoming events$/i) || dItem(/^events$/i);
      var evLi = plainItem(evHref, 'Events');
      if (dEvents) dRoot.replaceChild(evLi, dEvents); else dRoot.appendChild(evLi);
      dRoot.insertBefore(evLi, dSell ? dSell.nextSibling : dRoot.firstChild);

      var dConsign = dItem(/^consignment$/i);
      if (dConsign) dRoot.insertBefore(dConsign, evLi.nextSibling);

      var dTcg = dItem(/^trading card games$/i);
      if (dTcg) {
        relabel(dTcg, 'Other TCG');
        /* the submenu's back button repeats the parent label */
        [].forEach.call(dTcg.querySelectorAll('.parent-link-back--button'), function (b) {
          for (var i = 0; i < b.childNodes.length; i++) {
            if (b.childNodes[i].nodeType === 3 && /trading card games/i.test(b.childNodes[i].textContent)) b.childNodes[i].textContent = ' Other TCG';
          }
        });
      }

      /* News beside TCG Accessories */
      var dAcc = dItem(/^tcg accessories$/i);
      if (dAcc && !dItem(/^news$/i)) dRoot.insertBefore(plainItem('/blogs/news', 'News'), dAcc.nextSibling);

      var dCommunity = dItem(/^community$/i);
      if (dCommunity) dCommunity.remove();

      if (discA) { discA.textContent = 'Join Discord'; discA.classList.add('derpy-mobile-discord'); }
      if (dDiscord) dRoot.appendChild(dDiscord);
    }

    /* Hovering a dropdown-less item (Events, Consignment, News, …) must not
       dim the page: the theme flags `menu-item-hover` on <theme-header> for
       EVERY top item, which fades in the megamenu backdrop even when there is
       no menu to show. Strip the flag whenever the hovered item is childless. */
    var th = document.querySelector('theme-header');
    if (th) {
      var clearDim = function () {
        /* the contains() gate is LOAD-BEARING: classList.remove() on an
           absent class still rewrites the class attribute, which re-fires
           this MutationObserver — without the gate that's an infinite
           microtask loop (hard-frozen tab) */
        if (!th.classList.contains('menu-item-hover')) return;
        var hovered = document.querySelector('.full-menu > ul > li:hover');
        if (hovered && !hovered.classList.contains('menu-item-has-children')) {
          th.classList.remove('menu-item-hover');
        }
      };
      if (window.MutationObserver) {
        new MutationObserver(clearDim).observe(th, { attributes: true, attributeFilter: ['class'] });
      }
      nav.addEventListener('mouseover', function () {
        clearDim();
        setTimeout(clearDim, 60); // in case the theme re-flags after its own handler
      });
    }
  }
  if (document.readyState !== 'loading') restructure();
  else document.addEventListener('DOMContentLoaded', restructure);
})();


/*
 * INFINITE GALLERY (V2 only — gated on <html data-derpy-infinite>, which only
 * the Claude V2 theme.liquid sets; dormant everywhere else).
 *
 * Composition: the gallery opens with 16 cards (2 clean rows of 8 on desktop,
 * 8 rows of 2 on mobile); the rest of page 1 plus prefetched pages sit in an
 * in-memory buffer, so scrolling appends instantly (fetches hide behind the
 * buffer). Loading is fully automatic all the way to the end of results —
 * the only button is a "Load more" fallback that appears after repeated
 * network failures. The URL quietly tracks the deepest fetched page (?page=N).
 *
 * Also mounts the contextual jump button: ↑ appears once you've scrolled;
 * tapping it remembers your spot and turns into ↓ to return there.
 */
(function () {
  'use strict';
  if (!document.documentElement.hasAttribute('data-derpy-infinite')) return;

  var INITIAL_VISIBLE = 16;
  var BATCH = 16;          // cards appended per trigger
  var LOW_WATER = 20;      // prefetch when buffer drops below this

  /* The feed's own path + query, captured up front: quick view pushes
     /products/<handle> into the address bar, and page fetches built from
     location.pathname then requested PRODUCT pages — they had no grid, so the
     feed decided the catalogue had ended (stuck at ~60 of 2,492). */
  var FEED_PATH = location.pathname;
  var feedQuery = location.search;
  function qp() {
    if (location.pathname === FEED_PATH) feedQuery = location.search;
    return new URLSearchParams(feedQuery);
  }
  function pageUrl(n) {
    var p = qp();
    if (n <= 1) p.delete('page'); else p.set('page', String(n));
    var qs = p.toString();
    return FEED_PATH + (qs ? '?' + qs : '');
  }
  /* The feed no longer writes ?page=N into the address bar: reload/Back then
     reopened mid-list with every earlier result unreachable. Legacy ?page=N
     links on the app's pages (noindex) restart at the top instead.
     /collections pagination is left alone — crawlers use it. */
  (function () {
    var sp = new URLSearchParams(location.search);
    if ((parseInt(sp.get('page') || '1', 10) || 1) > 1 && /^\/a\/search(\/|$)/.test(location.pathname)) {
      sp.delete('page');
      var qs = sp.toString();
      location.replace(location.pathname + (qs ? '?' + qs : '') + location.hash);
    }
  })();
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- contextual jump button (top ⇄ return) ---------- */
  function mountJump() {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'derpy-jump';
    btn.setAttribute('aria-label', 'Back to top');
    btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="5 14 12 7 19 14"></polyline></svg>';
    document.body.appendChild(btn);
    var returnY = null, jumping = false, jumpTimer = null;
    function setState(s) {
      btn.classList.toggle('is-visible', s !== 'hidden');
      btn.classList.toggle('is-return', s === 'return');
      btn.setAttribute('aria-label', s === 'return' ? 'Return to where you were' : 'Back to top');
    }
    function onScroll() {
      if (jumping) return;
      var y = window.scrollY || document.documentElement.scrollTop;
      if (returnY !== null) {
        // user re-engaged with the top of the page → drop the return offer
        if (y > 400) { returnY = null; setState(y > window.innerHeight * 1.2 ? 'top' : 'hidden'); }
        else setState('return');
        return;
      }
      setState(y > window.innerHeight * 1.2 ? 'top' : 'hidden');
    }
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; onScroll(); });
    }, { passive: true });
    /* rAF glide — native behavior:'smooth' is unreliable with some
       extensions/embeds, so animate the scroll ourselves (easeOutCubic).
       The done callback (not a timer) releases the state machine, so the
       "return" state can't be cancelled by a slow animation. */
    function glide(toY, done) {
      if (reducedMotion) { window.scrollTo(0, toY); if (done) done(); return; }
      var from = window.scrollY || document.documentElement.scrollTop;
      var delta = toY - from;
      if (!delta) { if (done) done(); return; }
      var dur = Math.min(650, 250 + Math.abs(delta) * 0.05);
      var t0 = performance.now();
      function step(now) {
        var p = Math.min(1, (now - t0) / dur);
        var e = 1 - Math.pow(1 - p, 3);
        window.scrollTo(0, Math.round(from + delta * e));
        if (p < 1) requestAnimationFrame(step);
        else if (done) done();
      }
      requestAnimationFrame(step);
    }
    btn.addEventListener('click', function () {
      jumping = true;
      clearTimeout(jumpTimer);
      var release = function () { jumping = false; onScroll(); };
      jumpTimer = setTimeout(release, 4000); // safety net only — done() is the real release
      if (btn.classList.contains('is-return') && returnY !== null) {
        var target = returnY; returnY = null;
        glide(target, release);
      } else {
        returnY = window.scrollY || document.documentElement.scrollTop;
        glide(0, release);
      }
    });
    onScroll();
  }

  /* ---------- infinite gallery ---------- */
  window.DerpyInf = { state: 'loaded' };
  function boot() {
    var grid = document.getElementById('product-grid');
    if (!grid) { window.DerpyInf.state = 'no-grid'; return; }
    var pag = document.querySelector('pagination-theme.pagination'); // optional
    var totalResults = (function () {
      var m = document.documentElement.innerHTML.match(/"total":(\d+)/);
      return m ? parseInt(m[1], 10) : 0;
    })();
    var pageSize = (function () {
      var m = document.documentElement.innerHTML.match(/"pageSize":(\d+)/);
      return m ? parseInt(m[1], 10) : 20;
    })();
    var totalPages = 0;
    if (pag) {
      var nums = [].map.call(pag.querySelectorAll('ul.page-numbers li a, ul.page-numbers li span.page'), function (el) {
        return parseInt(el.textContent, 10);
      }).filter(function (n) { return !isNaN(n); });
      totalPages = nums.length ? Math.max.apply(null, nums) : 0;
    }
    if (!totalPages && totalResults) totalPages = Math.ceil(totalResults / pageSize);
    if (!totalPages) totalPages = 1;

    var fetchedPage = parseInt(qp().get('page') || '1', 10) || 1;
    var seen = {};
    [].forEach.call(grid.querySelectorAll('a.product-card--image-link'), function (a) {
      seen[(a.getAttribute('href') || '').split('?')[0]] = 1;
    });

    var pending = [];         // detached, ready-to-append card cells (in order)
    var fetching = false, done = false;
    var busy = false;         // an append (or its buffer-wait) is in flight
    var fetchFails = 0;       // consecutive page-fetch failures
    var epoch = 0;            // bumped on sort/filter reset — stale fetches discard themselves
    var waitInt = null;       // appendBatch's buffer-wait interval (cleared on reset)
    var selfMutation = false; // our own grid appends must not look like an app re-render
    function markSelf(fn) {
      selfMutation = true;
      try { fn(); } finally {
        (window.queueMicrotask || function (f) { Promise.resolve().then(f); })(function () { selfMutation = false; });
      }
    }

    /* trim the opening view to 16 cards; the extras seed the buffer */
    (function () {
      var cells = [].slice.call(grid.children);
      var cardCount = 0, cutIndex = -1;
      for (var i = 0; i < cells.length; i++) {
        if (cells[i].querySelector && cells[i].querySelector('.product-card, product-card')) {
          cardCount++;
          if (cardCount === INITIAL_VISIBLE) { cutIndex = i; break; }
        }
      }
      if (cutIndex > -1) {
        for (var j = cutIndex + 1; j < cells.length; j++) {
          if (cells[j].querySelector && cells[j].querySelector('.product-card, product-card')) {
            pending.push(grid.removeChild(cells[j]));
          }
        }
      }
    })();

    if (totalPages < 2 && !pending.length) return; // single short page — nothing to do

    var bar = document.createElement('div');
    bar.className = 'derpy-inf';
    bar.innerHTML = '<div class="derpy-inf__spin" hidden></div>' +
      '<button type="button" class="derpy-inf__more" hidden>Load more</button>' +
      '<div class="derpy-inf__count"></div>';
    if (pag) {
      pag.parentNode.insertBefore(bar, pag);
      pag.style.display = 'none';
    } else {
      grid.parentNode.insertBefore(bar, grid.nextSibling);
    }
    window.DerpyInf.state = 'booted';
    var spin = bar.querySelector('.derpy-inf__spin');
    var moreBtn = bar.querySelector('.derpy-inf__more');
    var countEl = bar.querySelector('.derpy-inf__count');

    function shownCount() { return grid.querySelectorAll('.product-card, product-card').length; }
    function updateCount(suffix) {
      countEl.textContent = 'Showing ' + shownCount() + (totalResults ? ' of ' + totalResults : '') + ' items' + (suffix || '');
      var tb = document.querySelector('.derpy-tb-count');
      if (tb && !tb.hasAttribute('data-total')) tb.textContent = shownCount() + ' shown'; // a known total ("437 results") stays
    }
    function reveal(scope) {
      [].forEach.call((scope || grid).querySelectorAll('.product-card--image-primary:not([data-derpy-revealed]), .product-card--image:not([data-derpy-revealed])'), function (el) {
        el.style.setProperty('opacity', '1', 'important');
        el.style.setProperty('visibility', 'visible', 'important');
        el.style.setProperty('transform', 'none', 'important');
        el.setAttribute('data-derpy-revealed', '1');
      });
    }
    function exhausted() { return fetchedPage >= totalPages && pending.length === 0; }
    function finish() {
      done = true;
      spin.hidden = true;
      moreBtn.hidden = true;
      updateCount(' — end of results');
      window.DerpyInf.state = 'done n=' + shownCount();
    }

    function ensureBuffer() {
      if (fetching || fetchedPage >= totalPages) return;
      if (pending.length >= LOW_WATER) return;
      fetching = true;
      var myEpoch = epoch; // a sort/filter reset mid-flight invalidates this fetch
      fetch(pageUrl(fetchedPage + 1), { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          fetching = false;
          if (myEpoch !== epoch) return; // ordering changed while we were fetching — discard
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var g = doc.getElementById('product-grid');
          var cells = g ? [].slice.call(g.children) : [];
          var raw = 0; // cards on the fetched page, dedupe aside
          for (var i = 0; i < cells.length; i++) {
            var a = cells[i].querySelector && cells[i].querySelector('a.product-card--image-link');
            if (!a) continue;
            raw++;
            var key = (a.getAttribute('href') || '').split('?')[0];
            if (!key || seen[key]) continue;
            seen[key] = 1;
            pending.push(document.importNode(cells[i], true));
          }
          if (!raw) { totalPages = fetchedPage; return; } // walked off the end (filtered sets are shorter)
          fetchedPage++;
          fetchFails = 0;
          ensureBuffer(); // top up further if a big append drained us
        })
        .catch(function () {
          fetching = false;
          if (myEpoch !== epoch) return;
          fetchFails++;
          window.DerpyInf.state = 'fetch-failed x' + fetchFails + ' page=' + (fetchedPage + 1);
        });
    }

    function appendBatch(fromButton) {
      if (done || busy) return;
      if (!pending.length) {
        if (exhausted()) { finish(); return; }
        // buffer empty but more pages exist → spinner while the fetch lands.
        // The wait loop RE-REQUESTS the fetch (a network hiccup must pause
        // the feed, not end it); after several consecutive failures it falls
        // back to a manual "Load more" button instead of spinning forever.
        busy = true;
        if (!fetching) ensureBuffer();
        spin.hidden = false;
        waitInt = setInterval(function () {
          if (pending.length || exhausted()) {
            clearInterval(waitInt); waitInt = null;
            busy = false;
            spin.hidden = true;
            if (pending.length) appendBatch(fromButton); else finish();
            return;
          }
          if (fetchFails >= 4) {
            clearInterval(waitInt); waitInt = null;
            busy = false;
            spin.hidden = true;
            moreBtn.hidden = false;
            window.DerpyInf.state = 'paused-after-errors x' + fetchFails;
            return;
          }
          if (!fetching) ensureBuffer();
        }, 300);
        return;
      }
      var frag = document.createDocumentFragment();
      var n = Math.min(BATCH, pending.length);
      for (var i = 0; i < n; i++) {
        var cell = pending.shift();
        cell.classList.add('derpy-inf-new');
        frag.appendChild(cell);
      }
      markSelf(function () { grid.appendChild(frag); });
      reveal();
      try { if (window.DerpyInitPrices) window.DerpyInitPrices(); } catch (e) {}
      updateCount();
      window.DerpyInf.state = 'appended n=' + shownCount() + ' buf=' + pending.length + ' fetched=' + fetchedPage;
      if (exhausted()) { finish(); return; }
      ensureBuffer();
      setTimeout(maybeLoad, 120); // chain-fill: keep appending while still near the bottom
    }

    moreBtn.addEventListener('click', function () {
      fetchFails = 0; // a manual click re-arms fetch retries after an error pause
      moreBtn.hidden = true;
      appendBatch(true);
    });

    /* Load trigger — deterministic position check on every (throttled) scroll
       frame, NOT an IntersectionObserver. IO only fires on boundary CROSSINGS:
       with any usefully large margin the sentinel can end up permanently
       inside the trigger zone after an append, so no further crossings occur
       and the feed stalls (the frozen-at-32 prod bug). A position check has
       no such failure mode. Throttling is timer-based, not rAF — rAF is
       suspended in background/unfocused tabs. */
    var armed = false;
    /* A sort/filter re-render can replace an ANCESTOR of the grid (search
       renders two nested #ProductGridContainer and the outer one's innerHTML
       is swapped). Our grid/bar are then detached: a detached bar's rect.top
       is 0, which read as "near the bottom", so the feed appended every page
       into the dead grid while the visible results froze at 16–20. */
    function adoptLiveGrid() {
      var g2 = document.getElementById('product-grid');
      if (!g2 || g2 === grid) return false;
      grid = g2;
      if (typeof observeGrid === 'function') observeGrid();
      return true;
    }
    function nearBottom() {
      if (!bar.isConnected) return false;
      return bar.getBoundingClientRect().top < window.innerHeight + 900;
    }
    function maybeLoad() {
      if (!grid.isConnected) { if (adoptLiveGrid()) reset(); return; }
      if (done || busy || !armed) return;
      if (!moreBtn.hidden) return; // paused — the network-failure "Load more" fallback is showing
      if (nearBottom()) appendBatch(false);
    }
    var lastCheck = 0, checkQueued = false;
    function onInfScroll() {
      var now = Date.now();
      if (now - lastCheck > 120) { lastCheck = now; maybeLoad(); return; }
      if (checkQueued) return;
      checkQueued = true;
      setTimeout(function () { checkQueued = false; lastCheck = Date.now(); maybeLoad(); }, 150);
    }
    window.addEventListener('scroll', onInfScroll, { passive: true });
    window.addEventListener('resize', onInfScroll, { passive: true });
    /* the opening view holds at exactly 16 — the loader arms on the first
       real scroll, not on page load */
    window.addEventListener('scroll', function armOnce() {
      window.removeEventListener('scroll', armOnce);
      armed = true;
      maybeLoad();
    }, { passive: true });

    /* ---- sort/filter ↔ pagination interplay ----
       The Cloud Search app re-renders the grid in place when the visitor
       changes sort or filters. Without handling, two things corrupt the feed:
       (1) our ?page=N deep-link tracking leaks into the app's next request,
           so the re-sorted view starts mid-list instead of at the top; and
       (2) our buffer + dedupe map belong to the OLD ordering, so pages of the
           new ordering get wrongly dropped as "already seen" — cards vanish.
       Handling: strip ?page the instant a sort/filter control is touched, and
       fully reset feed state whenever the grid changes by any hand but ours. */
    function stripPageParam() {
      if (location.pathname !== FEED_PATH) return; // quick view owns the address bar right now
      var p = qp();
      if (!p.has('page')) return;
      p.delete('page');
      var qs = p.toString();
      try { history.replaceState(history.state, '', FEED_PATH + (qs ? '?' + qs : '')); } catch (e) {}
    }
    document.addEventListener('change', function (e) {
      if (e.target && e.target.closest && e.target.closest('.derpy-toolbar, [class*="cloud-search"], facet-filters-form, #SortByBar, #SortByMobile')) stripPageParam();
    }, true);
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('[class*="cloud-search-filter"], .derpy-tb-chip, .derpy-tb-clear, .derpy-tb-toggle, .derpy-fd')) stripPageParam();
    }, true);

    function reset() {
      epoch++; // in-flight fetches of the old ordering discard themselves
      if (waitInt) { clearInterval(waitInt); waitInt = null; }
      pending = [];
      seen = {};
      [].forEach.call(grid.querySelectorAll('a.product-card--image-link'), function (a) {
        seen[(a.getAttribute('href') || '').split('?')[0]] = 1;
      });
      fetchedPage = parseInt(qp().get('page') || '1', 10) || 1;
      totalPages = 9e9;  // unknown for the new ordering — the empty-page probe ends the feed
      totalResults = 0;  // unknown too; count label falls back to "Showing N items"
      done = false; busy = false; fetchFails = 0;
      spin.hidden = true;
      moreBtn.hidden = true;
      /* the re-render brings back numbered pagination — hide it again and
         re-seat the bar next to the LIVE grid */
      pag = document.querySelector('pagination-theme.pagination');
      if (pag) { pag.style.display = 'none'; if (bar.parentNode !== pag.parentNode || bar.nextSibling !== pag) pag.parentNode.insertBefore(bar, pag); }
      else if (!bar.isConnected || bar.previousSibling !== grid) grid.parentNode.insertBefore(bar, grid.nextSibling);
      reveal();
      try { if (window.DerpyInitPrices) window.DerpyInitPrices(); } catch (e) {}
      updateCount();
      window.DerpyInf.state = 'reset n=' + shownCount();
      ensureBuffer();
    }
    var resetTimer = null;
    function scheduleReset() {
      clearTimeout(resetTimer);
      resetTimer = setTimeout(reset, 200); // debounced — rapid sort toggles re-render repeatedly
    }
    var gridMO = null;
    function observeGrid() {
      if (!window.MutationObserver) return;
      if (gridMO) gridMO.disconnect();
      gridMO = new MutationObserver(function () { if (!selfMutation) scheduleReset(); });
      gridMO.observe(grid, { childList: true });
    }
    if (window.MutationObserver) {
      observeGrid();
      /* watch a STABLE ancestor for the grid being swapped out — its own
         parent can be detached along with it */
      new MutationObserver(function () {
        if (adoptLiveGrid()) scheduleReset();
      }).observe(document.getElementById('main-content') || document.body, { childList: true, subtree: true });
    }

    updateCount();
    ensureBuffer(); // prefetch page 2 immediately — first appends are instant
    mountJump();
  }

  /* Wait until the app has rendered the grid. Pagination markup is a nice
     signal that the render is done, but it's OPTIONAL — on slow machines /
     strict-privacy browsers it can arrive very late or not at all, so we
     also boot once the card count has been stable for ~1s. No hard timeout
     (checks back off to 1s ticks; hard stop only after ~10 minutes). */
  var tries = 0, lastCount = -1, stable = 0;
  window.DerpyInf.state = 'waiting';
  function tick() {
    /* galleries/search only — elsewhere this loop used to poll for ~10 minutes */
    if (!document.documentElement.classList.contains('derpy-grid-page')) { window.DerpyInf.state = 'not-a-gallery'; return; }
    tries++;
    var cards = document.querySelectorAll('#product-grid .product-card, #product-grid product-card').length;
    window.DerpyInf.state = 'waiting cards=' + cards + ' t=' + tries;
    if (cards > 0 && document.querySelector('pagination-theme.pagination')) { boot(); return; }
    if (cards > 0 && cards === lastCount) stable++; else stable = 0;
    lastCount = cards;
    if (cards > 0 && stable >= 4) { boot(); return; } // settled ~1s, no pagination needed
    if (tries < 60) setTimeout(tick, 250);
    else if (tries < 600) setTimeout(tick, 1000);
    else window.DerpyInf.state = 'gave-up';
  }
  if (document.readyState !== 'loading') tick();
  else document.addEventListener('DOMContentLoaded', tick);
})();


/*
 * PDP composition (desktop): the media column had a tall empty run under the
 * card stage. Pull the "You may also like" + "Recently viewed" sections up
 * into that column as compact panels beside the buy module. Mobile keeps the
 * theme's normal stacked flow (no relocation below 990px).
 */
(function () {
  'use strict';
  function relocate() {
    try {
      if (!window.matchMedia('(min-width: 1068px)').matches) return; // theme's two-column PDP starts at 1068px
      if (!/^\/products\//.test(location.pathname)) return;
      var container = document.querySelector('.product-section .product-image-container');
      if (!container) return;
      var mediaCol = container.closest('.product-images') || container.parentElement;
      if (!mediaCol) return;
      var moved = false;
      ['[id*="product-recommendations"]', '.section-recently-viewed-products'].forEach(function (sel) {
        var sec = document.querySelector('.shopify-section' + (sel[0] === '[' ? sel : sel) );
        if (!sec) sec = document.querySelector(sel);
        if (sec && !mediaCol.contains(sec)) {
          sec.classList.add('derpy-side-panel');
          mediaCol.appendChild(sec);
          moved = true;
        }
      });
      if (moved) document.body.classList.add('derpy-pdp-side');
    } catch (e) {}
  }
  if (document.readyState !== 'loading') setTimeout(relocate, 400);
  else document.addEventListener('DOMContentLoaded', function () { setTimeout(relocate, 400); });
})();



/*
 * Email capture — "be first to the latest drops" popup.
 * One gentle ask per visitor: 45s dwell OR 35% scroll (whichever first),
 * never on cart/checkout/account, never while a quick-view/zoom overlay is
 * open, once per session, 30-day cooldown after dismissal, never again after
 * subscribing. Submits Shopify's standard newsletter form (form_type=
 * customer): tries fetch first for an inline thank-you; if Shopify demands
 * its captcha challenge, falls back to a real form post so the subscription
 * still completes.
 */
(function () {
  'use strict';
  var LS_DONE = 'derpyEmailPopDone', LS_AT = 'derpyEmailPopAt';
  try {
    /* Back from the (navigation) signup post → mark done + visible thank-you.
       Two success markers: our own `derpy_signed_up=1` (planted in return_to —
       Shopify redirects there VERBATIM on success and does NOT append its own
       param when return_to is set) and `customer_posted=true` (belt & braces
       for the referrer-based flow). */
    if (/[?&](derpy_signed_up=1|customer_posted=true)/.test(location.search)) {
      localStorage.setItem(LS_DONE, '1');
      var mountToast = function () {
        var t = document.createElement('div');
        t.className = 'derpy-pop-toast';
        t.setAttribute('role', 'status');
        t.innerHTML = '&#127881; You&rsquo;re on the list! Watch your inbox for the next drop.';
        document.body.appendChild(t);
        requestAnimationFrame(function () { t.classList.add('is-on'); });
        setTimeout(function () {
          t.classList.remove('is-on');
          setTimeout(function () { if (t.parentNode) t.parentNode.removeChild(t); }, 400);
        }, 6500);
      };
      if (document.readyState !== 'loading') mountToast();
      else document.addEventListener('DOMContentLoaded', mountToast);
      /* tidy the URL so the params don't linger in copied links */
      try {
        var sp = new URLSearchParams(location.search);
        sp.delete('customer_posted');
        sp.delete('derpy_signed_up');
        var qs = sp.toString();
        history.replaceState(null, '', location.pathname + (qs ? '?' + qs : '') + location.hash);
      } catch (e2) {}
      return;
    }
    if (localStorage.getItem(LS_DONE)) return;
    var at = parseInt(localStorage.getItem(LS_AT) || '0', 10);
    if (at && Date.now() - at < 30 * 864e5) return;
    if (sessionStorage.getItem('derpyEmailPopSeen')) return;
  } catch (e) { return; }
  if (/^\/(cart|checkout|challenge|account)/.test(location.pathname)) return;
  if (window.Shopify && window.Shopify.designMode) return;

  var shown = false, dwellTimer, lastFocus = null;

  function overlayOpen() {
    return !!document.querySelector('.side-panel.active, body.derpy-zoom-lock, .derpy-zoom');
  }

  function close(dismissed) {
    var ov = document.querySelector('.derpy-pop-overlay');
    if (!ov) return;
    ov.classList.remove('is-open');
    setTimeout(function () { if (ov.parentNode) ov.parentNode.removeChild(ov); }, 250);
    document.removeEventListener('keydown', onKey);
    if (dismissed) { try { localStorage.setItem(LS_AT, String(Date.now())); } catch (e) {} }
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
  }
  function onKey(e) {
    if (e.key === 'Escape') { close(true); return; }
    if (e.key !== 'Tab') return;
    /* aria-modal: keep Tab / Shift+Tab inside the popup */
    var pop = document.querySelector('.derpy-pop');
    if (!pop) return;
    var f = [].filter.call(pop.querySelectorAll('button, input, a[href], [tabindex]:not([tabindex="-1"])'), function (el) {
      return !el.disabled && !el.hidden && el.offsetParent !== null;
    });
    if (!f.length) return;
    /* position among the controls; -1 = focus is on the dialog itself (the
       phone default, or after clicking popup text) or outside it — wrap too */
    var idx = f.indexOf(document.activeElement);
    if (e.shiftKey && idx <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
    else if (!e.shiftKey && (idx === -1 || idx === f.length - 1)) { e.preventDefault(); f[0].focus(); }
  }

  function show() {
    if (shown) return;
    if (overlayOpen()) { dwellTimer = setTimeout(show, 25000); return; }
    shown = true;
    try { sessionStorage.setItem('derpyEmailPopSeen', '1'); } catch (e) {}
    lastFocus = document.activeElement;

    var ov = document.createElement('div');
    ov.className = 'derpy-pop-overlay';
    ov.innerHTML =
      '<div class="derpy-pop" role="dialog" aria-modal="true" aria-label="Email signup" tabindex="-1">' +
        '<button type="button" class="derpy-pop__close" aria-label="Close">&#10005;</button>' +
        '<div class="derpy-pop__emoji">&#128230;</div>' +
        '<h3 class="derpy-pop__title">Be first to the latest drops</h3>' +
        '<p class="derpy-pop__sub">New sets, restocks and preorder alerts from Derpy Cards &mdash; a couple of emails a month, no spam.</p>' +
        '<form class="derpy-pop__form" method="post" action="/contact#derpy-pop" accept-charset="UTF-8">' +
          '<input type="hidden" name="form_type" value="customer">' +
          '<input type="hidden" name="utf8" value="&#10003;">' +
          '<input type="hidden" name="contact[tags]" value="newsletter,popup">' +
          '<input type="hidden" name="return_to" value="">' +
          '<input class="derpy-pop__email" type="email" name="contact[email]" placeholder="your@email.com" required autocomplete="email" aria-label="Email address">' +
          '<button type="submit" class="derpy-pop__go">Keep me posted</button>' +
        '</form>' +
        '<div class="derpy-pop__msg" hidden></div>' +
        '<button type="button" class="derpy-pop__no">No thanks</button>' +
        '<div class="derpy-pop__fine">Unsubscribe anytime.</div>' +
      '</div>';
    document.body.appendChild(ov);
    requestAnimationFrame(function () { ov.classList.add('is-open'); });

    var pop = ov.querySelector('.derpy-pop');
    var form = ov.querySelector('.derpy-pop__form');
    var email = ov.querySelector('.derpy-pop__email');
    var msg = ov.querySelector('.derpy-pop__msg');

    ov.addEventListener('click', function (e) { if (e.target === ov) close(true); });
    ov.querySelector('.derpy-pop__close').addEventListener('click', function () { close(true); });
    ov.querySelector('.derpy-pop__no').addEventListener('click', function () { close(true); });
    document.addEventListener('keydown', onKey);
    if (window.matchMedia('(pointer: fine)').matches) email.focus();
    else pop.focus();

    /* Submit is a REAL navigation post — Shopify rejects scripted (fetch/XHR)
       posts to /contact on this store with 400 no matter the encoding, so an
       AJAX flow can never succeed. The browser posts, Shopify redirects back
       here with ?customer_posted=true, and the module's return branch shows
       the thank-you toast and marks the visitor done. `return_to` pins the
       comeback to this exact page. */
    form.addEventListener('submit', function () {
      /* Build return_to at submit time (on gallery pages the URL's ?page=N
         moves as the visitor scrolls) and plant our own success marker in it —
         Shopify only redirects to return_to when the signup succeeded, so
         landing back on a URL carrying the marker IS the confirmation. */
      var sp = new URLSearchParams(location.search);
      sp.set('derpy_signed_up', '1');
      form.querySelector('[name="return_to"]').value = location.pathname + '?' + sp.toString();
      var go = ov.querySelector('.derpy-pop__go');
      go.disabled = true;
      go.textContent = 'Signing you up…';
      msg.hidden = true;
    });
  }

  dwellTimer = setTimeout(show, 45000);
  function onScroll() {
    var d = document.documentElement;
    var depth = (window.scrollY + window.innerHeight) / Math.max(1, d.scrollHeight);
    if (window.scrollY > 600 && depth > 0.35) {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(dwellTimer);
      show();
    }
  }
  window.addEventListener('scroll', onScroll, { passive: true });
})();
